import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { quizScriptApi } from '@/api/quizScript'
import { QuizPreviewPanel } from './QuizPreviewPanel'

type QuizLocation = 'selected' | 'review' | 'checkup' | 'block' | 'applied' | 'explanation' | 'wrapup'

const QUIZ_LOCATIONS: { value: QuizLocation; label: string; color: string }[] = [
  { value: 'selected', label: '진도', color: 'bg-blue-100 text-blue-700' },
  { value: 'review', label: '복습', color: 'bg-green-100 text-green-700' },
  { value: 'checkup', label: '체크업', color: 'bg-teal-100 text-teal-700' },
  { value: 'block', label: '블록퀴즈', color: 'bg-purple-100 text-purple-700' },
  { value: 'applied', label: '실전연습', color: 'bg-amber-100 text-amber-700' },
  { value: 'explanation', label: '설명하기', color: 'bg-rose-100 text-rose-700' },
  { value: 'wrapup', label: '성인', color: 'bg-slate-100 text-slate-700' },
]

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

function InlineDebugId({ quizId }: { quizId: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(quizId)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-mono">
      <button
        type="button"
        onClick={handleCopy}
        className="px-1.5 py-0.5 text-slate-500 bg-slate-100 rounded-l hover:bg-slate-200 hover:text-slate-700 transition-colors"
        title="Copy quiz ID"
      >
        {copied ? 'copied!' : quizId}
      </button>
      <button
        type="button"
        onClick={() => window.open(`https://adm.quebon.tv/quiz?type=&category=id&by=modified&findText=${quizId}`, '_blank')}
        className="px-1 py-0.5 text-slate-500 bg-slate-100 rounded-r hover:bg-slate-200 hover:text-slate-700 transition-colors"
        title="View in legacy admin"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
          <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Zm7.25-.75a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0V6.31l-5.47 5.47a.75.75 0 1 1-1.06-1.06l5.47-5.47H12.25a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
        </svg>
      </button>
    </span>
  )
}

export function QuizPanel({ lessonId }: { lessonId: number }) {
  const [activeLocation, setActiveLocation] = useState<QuizLocation>('selected')
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null)

  const handleLocationChange = (loc: QuizLocation) => {
    setActiveLocation(loc)
    setSelectedQuizId(null)
  }

  const { data: quizzes, isLoading } = useQuery({
    queryKey: ['inspection', 'quizzes', lessonId, activeLocation],
    queryFn: () => quizScriptApi.getContentQuizzes(lessonId, activeLocation),
    staleTime: 5 * 60 * 1000,
  })

  const locationCounts = useQuery({
    queryKey: ['inspection', 'quizCounts', lessonId],
    queryFn: async () => {
      const counts: Record<string, number> = {}
      const results = await Promise.all(
        QUIZ_LOCATIONS.map(async loc => {
          const data = await quizScriptApi.getContentQuizzes(lessonId, loc.value)
          return { key: loc.value, count: data.length }
        })
      )
      for (const r of results) counts[r.key] = r.count
      return counts
    },
    staleTime: 5 * 60 * 1000,
  })

  const hasQuizzes = !!quizzes && quizzes.length > 0

  return (
    <div>
      {/* Sticky 필터 헤더 (스크롤 시 상단 고정) */}
      <div className="sticky top-0 z-30 space-y-3 border-b border-slate-200 bg-slate-50/95 px-6 pb-3 pt-6 backdrop-blur">
        {/* Location 탭 */}
        <div className="flex flex-wrap gap-2">
          {QUIZ_LOCATIONS.map(loc => {
            const count = locationCounts.data?.[loc.value]
            return (
              <button
                key={loc.value}
                onClick={() => handleLocationChange(loc.value)}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all',
                  activeLocation === loc.value
                    ? 'bg-white shadow-sm ring-1 ring-slate-200 text-slate-900'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                )}
              >
                {loc.label}
                {count !== undefined && (
                  <span className={cn(
                    'rounded-full px-1.5 py-0.5 text-xs font-bold',
                    count > 0 ? loc.color : 'bg-slate-100 text-slate-400'
                  )}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* 번호 chip 네비게이션 */}
        {hasQuizzes && (
          <div className="flex flex-wrap gap-1.5">
            {quizzes.map((quiz, i) => {
              const id = String(quiz.quizId)
              const selected = id === selectedQuizId
              return (
                <button
                  key={id}
                  onClick={() => setSelectedQuizId(prev => prev === id ? null : id)}
                  className={cn(
                    'w-7 h-7 inline-flex items-center justify-center text-xs font-medium rounded-full border transition-colors',
                    selected
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-amber-400 hover:bg-amber-50'
                  )}
                >
                  {i + 1}
                </button>
              )
            })}
            {selectedQuizId && (
              <button
                onClick={() => setSelectedQuizId(null)}
                className="px-2 h-7 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
              >
                전체
              </button>
            )}
          </div>
        )}
      </div>

      {/* 퀴즈 목록 본문 */}
      <div className="px-6 py-4">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : !hasQuizzes ? (
          <div className="py-12 text-sm text-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-400">
            이 위치에 퀴즈가 없습니다
          </div>
        ) : (
          <div className="space-y-3">
            {quizzes
              .filter(q => !selectedQuizId || String(q.quizId) === selectedQuizId)
              .map((quiz, i) => {
                const id = String(quiz.quizId)
                return (
                  <div key={id} className="p-3 bg-white rounded-lg border border-slate-200 space-y-2">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400 font-mono">{i + 1}.</span>
                      <InlineDebugId quizId={id} />
                    </div>
                    <QuizPreviewPanel quizId={id} />
                  </div>
                )
              })}
          </div>
        )}
      </div>
    </div>
  )
}
